"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { useRouter } from "nextjs-toploader/app"

import {
  freshenSplitPaneTarget,
  parseSplitPaneHref,
  splitPaneTargetToHref,
  type SplitPaneId,
  type SplitPaneTarget
} from "@/lib/split-pane/targets"
import {
  areSplitPaneTargetsEqual,
  closeSplitPane,
  emptySplitPaneState,
  isSplitPaneMode,
  openSplitPaneTarget,
  type SplitPaneState
} from "@/lib/split-pane/state"

interface SplitPaneContextValue {
  activePaneId: SplitPaneId
  closePane: (paneId: SplitPaneId) => void
  currentRouteTarget: SplitPaneTarget | null
  expandPane: (paneId: SplitPaneId) => void
  isSplitMode: boolean
  navigateToHref: (href: string) => void
  openHrefInPane: (href: string, paneId: SplitPaneId) => void
  openHrefInSecondaryPane: (href: string) => void
  openTargetInPane: (target: SplitPaneTarget, paneId: SplitPaneId) => void
  panes: SplitPaneState
  setActivePane: (paneId: SplitPaneId) => void
}

const SplitPaneContext = createContext<SplitPaneContextValue | null>(null)

const getBrowserOrigin = () =>
  typeof window === "undefined" ? "http://localhost" : window.location.origin

export function SplitPaneProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const searchSnapshot = searchParams?.toString() ?? ""
  const routeHref = `${pathname || "/"}${searchSnapshot ? `?${searchSnapshot}` : ""}`
  const currentRouteTarget = useMemo(
    () => parseSplitPaneHref(routeHref, getBrowserOrigin()),
    [routeHref]
  )
  const currentRouteTargetRef = useRef<SplitPaneTarget | null>(currentRouteTarget)
  const panesRef = useRef<SplitPaneState>(emptySplitPaneState)
  const [panes, setPanes] = useState<SplitPaneState>(() => ({
    ...emptySplitPaneState,
    left: currentRouteTarget
  }))
  const [activePaneId, setActivePaneId] = useState<SplitPaneId>("left")
  const isSplitMode = isSplitPaneMode(panes)

  useEffect(() => {
    currentRouteTargetRef.current = currentRouteTarget
  }, [currentRouteTarget])

  useEffect(() => {
    panesRef.current = panes
  }, [panes])

  useEffect(() => {
    if (isSplitMode || !currentRouteTarget) {
      return
    }

    setPanes(currentPanes => {
      if (
        areSplitPaneTargetsEqual(currentPanes.left, currentRouteTarget) &&
        !currentPanes.right
      ) {
        return currentPanes
      }

      return {
        left: currentRouteTarget,
        right: null
      }
    })
    setActivePaneId("left")
  }, [currentRouteTarget, isSplitMode])

  const pushTargetRoute = useCallback(
    (target: SplitPaneTarget) => {
      router.push(splitPaneTargetToHref(target))
    },
    [router]
  )

  const openTargetInPane = useCallback(
    (target: SplitPaneTarget, paneId: SplitPaneId) => {
      const nextTarget = freshenSplitPaneTarget(target)

      setPanes(currentPanes => {
        return openSplitPaneTarget({
          currentRouteTarget: currentRouteTargetRef.current,
          paneId,
          panes: currentPanes,
          target: nextTarget
        })
      })
      setActivePaneId(paneId)
    },
    []
  )

  const openHrefInPane = useCallback(
    (href: string, paneId: SplitPaneId) => {
      const target = parseSplitPaneHref(href, getBrowserOrigin())

      if (!target) {
        router.push(href)
        return
      }

      openTargetInPane(freshenSplitPaneTarget(target), paneId)
    },
    [openTargetInPane, router]
  )

  const openHrefInSecondaryPane = useCallback(
    (href: string) => {
      openHrefInPane(href, "right")
    },
    [openHrefInPane]
  )

  const navigateToHref = useCallback(
    (href: string) => {
      const trimmed = typeof href === 'string' ? href.trim() : ''
      // Fail-closed: empty Discuss/Go seeds must not router.push('').
      if (!trimmed) return

      const target = parseSplitPaneHref(trimmed, getBrowserOrigin())

      if (!target) {
        router.push(trimmed)
        return
      }

      if (!panesRef.current.left || !panesRef.current.right) {
        router.push(splitPaneTargetToHref(target))
        return
      }

      openTargetInPane(freshenSplitPaneTarget(target), activePaneId)
    },
    [activePaneId, openTargetInPane, router]
  )

  const closePane = useCallback(
    (paneId: SplitPaneId) => {
      const result = closeSplitPane({
        paneId,
        panes: panesRef.current
      })

      if (!result.remainingTarget) {
        setPanes(emptySplitPaneState)
        setActivePaneId("left")
        return
      }

      setPanes(result.panes)
      setActivePaneId("left")
      pushTargetRoute(result.remainingTarget)
    },
    [pushTargetRoute]
  )

  const expandPane = useCallback(
    (paneId: SplitPaneId) => {
      const target = panesRef.current[paneId]
      if (!target) return

      setPanes({
        left: target,
        right: null
      })
      setActivePaneId("left")
      pushTargetRoute(target)
    },
    [pushTargetRoute]
  )

  const setActivePane = useCallback((paneId: SplitPaneId) => {
    setActivePaneId(paneId)
  }, [])

  useEffect(() => {
    if (!isSplitMode) {
      return
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }

      const targetElement = event.target as Element | null
      const anchor = targetElement?.closest?.("a[href]") as HTMLAnchorElement | null

      if (!anchor || anchor.target || anchor.hasAttribute("download")) {
        return
      }

      const href = anchor.getAttribute("href")
      const paneTarget = parseSplitPaneHref(href, getBrowserOrigin())

      if (!href || !paneTarget) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      openTargetInPane(paneTarget, activePaneId)
    }

    document.addEventListener("click", handleDocumentClick, true)

    return () => {
      document.removeEventListener("click", handleDocumentClick, true)
    }
  }, [activePaneId, isSplitMode, openTargetInPane])

  useEffect(() => {
    if (!isSplitMode) {
      return
    }

    const handleOpenVirtualFile = (event: Event) => {
      const detail = (event as CustomEvent<{
        content?: string | null
        id?: string
        name?: string
        path?: string
      }>).detail

      if (!detail?.id || !detail.path || !detail.name) {
        return
      }

      event.preventDefault()
      openTargetInPane(
        {
          content: detail.content,
          id: detail.id,
          kind: "virtual-file",
          name: detail.name,
          path: detail.path
        },
        activePaneId
      )
    }

    window.addEventListener("openVirtualFileInCanvas", handleOpenVirtualFile, {
      capture: true
    })

    return () => {
      window.removeEventListener("openVirtualFileInCanvas", handleOpenVirtualFile, {
        capture: true
      })
    }
  }, [activePaneId, isSplitMode, openTargetInPane])

  const value = useMemo<SplitPaneContextValue>(
    () => ({
      activePaneId,
      closePane,
      currentRouteTarget,
      expandPane,
      isSplitMode,
      navigateToHref,
      openHrefInPane,
      openHrefInSecondaryPane,
      openTargetInPane,
      panes,
      setActivePane
    }),
    [
      activePaneId,
      closePane,
      currentRouteTarget,
      expandPane,
      isSplitMode,
      navigateToHref,
      openHrefInPane,
      openHrefInSecondaryPane,
      openTargetInPane,
      panes,
      setActivePane
    ]
  )

  return (
    <SplitPaneContext.Provider value={value}>
      {children}
    </SplitPaneContext.Provider>
  )
}

export function useSplitPaneNavigation() {
  const context = useContext(SplitPaneContext)
  const fallbackRouter = useRouter()

  if (!context) {
    return {
      activePaneId: "left" as SplitPaneId,
      closePane: () => {},
      currentRouteTarget: null,
      expandPane: () => {},
      isSplitMode: false,
      navigateToHref: (href: string) => fallbackRouter.push(href),
      openHrefInPane: (href: string, _paneId: SplitPaneId) =>
        fallbackRouter.push(href),
      openHrefInSecondaryPane: (href: string) => fallbackRouter.push(href),
      openTargetInPane: (_target: SplitPaneTarget, _paneId: SplitPaneId) => {},
      panes: emptySplitPaneState,
      setActivePane: (_paneId: SplitPaneId) => {}
    } satisfies SplitPaneContextValue
  }

  return context
}
